import { useCallback, useEffect, useState } from "react";
import { AlertCircle, Clock, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { FaAngleRight } from "react-icons/fa6";
import TimerImage from "../../../../Images/timeupimage.png";

const dummyQuestions = [
  {
    id: "dq-1",
    question: "Which number should come next in the series: 2, 4, 8, 16, ?",
    options: ["18", "24", "32", "64"],
    answer: "32",
  },
  {
    id: "dq-2",
    question: "What is 15% of 200?",
    options: ["15", "20", "30", "40"],
    answer: "30",
  },
  {
    id: "dq-3",
    question: "Which word is the synonym of 'Rapid'?",
    options: ["Slow", "Quick", "Late", "Weak"],
    answer: "Quick",
  },
  {
    id: "dq-4",
    question: "If A = 1, B = 2, C = 3, then what is the value of CAB?",
    options: ["312", "123", "321", "213"],
    answer: "312",
  },
  {
    id: "dq-5",
    question: "A triangle has how many sides?",
    options: ["2", "3", "4", "5"],
    answer: "3",
  },
];

const CONTEST_SECONDS = 60;

const buildDummyResult = (finalAnswers, finalTimeLeft) => {
  const rightCount = dummyQuestions.filter(
    (question) => finalAnswers[question.id] === question.answer
  ).length;
  const attemptedCount = dummyQuestions.filter(
    (question) => finalAnswers[question.id]
  ).length;
  const wrongCount = attemptedCount - rightCount;
  const skipCount = dummyQuestions.length - attemptedCount;
  const totalTimeTaken = CONTEST_SECONDS - finalTimeLeft;
  const score = Math.max(rightCount - wrongCount * 0.25, 0);
  const accuracy =
    attemptedCount > 0 ? Math.round((rightCount / attemptedCount) * 100) : 0;
  const userRank = 1;
  const winningAmount = 50;

  return {
    totalQuestions: dummyQuestions.length,
    rightCount,
    wrongCount,
    skipCount,
    score,
    finalScore: score,
    accuracy,
    userName: "Dummy User",
    contestId: "DUMMY-CONTEST-001",
    userRank,
    winningAmount,
    totalTimeTaken,
    avgTimePerQuestion:
      attemptedCount > 0 ? Math.round(totalTimeTaken / attemptedCount) : 0,
    avgTimeOnCorrect:
      rightCount > 0 ? Math.round(totalTimeTaken / rightCount) : 0,
    leaderboard: [
      {
        _id: "dummy-user",
        name: "You",
        score,
        contestScore: score,
        prizeWon: winningAmount,
      },
      {
        _id: "dummy-player-2",
        name: "Aarav Sharma",
        score: Math.max(score - 0.5, 0),
        contestScore: Math.max(score - 0.5, 0),
        prizeWon: 25,
      },
      {
        _id: "dummy-player-3",
        name: "Priya Verma",
        score: Math.max(score - 1, 0),
        contestScore: Math.max(score - 1, 0),
        prizeWon: 10,
      },
    ],
    submittedAt: new Date().toISOString(),
    resultDeclareTime: "Instant",
  };
};

const DummyContest = () => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(CONTEST_SECONDS);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [timeUp, setTimeUp] = useState(false);

  const currentQuestion = dummyQuestions[currentIndex];
  const isLastQuestion = currentIndex === dummyQuestions.length - 1;

  const navigateToResult = useCallback(
    (finalAnswers, finalTimeLeft) => {
      navigate("/dash/contest-result", {
        state: {
          isDummyContest: true,
          dummyResult: buildDummyResult(finalAnswers, finalTimeLeft),
        },
      });
    },
    [navigate]
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setTimeUp(true);
          setShowSubmitModal(false);
          navigateToResult(answers, 0);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [answers, navigateToResult]);

  const formatTimeLeft = (seconds) => {
    const total = Number(seconds || 0);
    const minutes = Math.floor(total / 60);
    const secs = total % 60;

    return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(
      2,
      "0"
    )}`;
  };

  const saveCurrentAnswer = () => {
    if (!selectedOption) return;

    setAnswers((prev) => ({
      ...prev,
      [currentQuestion.id]: selectedOption,
    }));
  };

  const goToNextQuestion = () => {
    saveCurrentAnswer();

    if (isLastQuestion) {
      const nextAnswers = selectedOption
        ? { ...answers, [currentQuestion.id]: selectedOption }
        : answers;
      navigateToResult(nextAnswers, timeLeft);
      return;
    }

    const nextIndex = currentIndex + 1;
    setCurrentIndex(nextIndex);
    setSelectedOption(answers[dummyQuestions[nextIndex].id] || null);
  };

  const handleSkip = () => {
    setAnswers((prev) => {
      const nextAnswers = { ...prev };
      delete nextAnswers[currentQuestion.id];
      return nextAnswers;
    });

    if (isLastQuestion) {
      const nextAnswers = { ...answers };
      delete nextAnswers[currentQuestion.id];
      navigateToResult(nextAnswers, timeLeft);
      return;
    }

    const nextIndex = currentIndex + 1;
    setCurrentIndex(nextIndex);
    setSelectedOption(answers[dummyQuestions[nextIndex].id] || null);
  };

  const handleSubmitContest = () => {
    saveCurrentAnswer();

    const nextAnswers = selectedOption
      ? { ...answers, [currentQuestion.id]: selectedOption }
      : answers;
    const hasUnanswered = dummyQuestions.some(
      (question) => !nextAnswers[question.id]
    );

    if (hasUnanswered && !timeUp) {
      setShowSubmitModal(true);
      return;
    }

    navigateToResult(nextAnswers, timeLeft);
  };

  const confirmSubmitContest = () => {
    const nextAnswers = selectedOption
      ? { ...answers, [currentQuestion.id]: selectedOption }
      : answers;
    setAnswers(nextAnswers);
    setShowSubmitModal(false);
    navigateToResult(nextAnswers, timeLeft);
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7] px-4 py-4 lg:pt-14">
      <div className="mx-auto flex min-h-[calc(100vh-32px)] w-full max-w-[430px] flex-col lg:max-w-[900px]">
        <div className="mb-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate("/dash/contest")}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-gray-500 shadow-sm"
          >
            <FaAngleRight size={18} className="rotate-180" />
          </button>

          <button
            type="button"
            onClick={handleSubmitContest}
            className="rounded-[10px] bg-[#562566] px-10 py-3 text-md font-bold text-white shadow-md"
          >
            Submit
          </button>
        </div>

        <div className="mb-3 rounded-[30px] border border-[#E7E7E7] bg-gray-100 px-4 py-3 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 rounded-full bg-yellow-50 px-3 py-2">
              <Clock size={18} className="text-[#FF4D4F]" />
              <span className="text-sm font-bold text-[#FF4D4F]">
                {formatTimeLeft(timeLeft)}
              </span>
            </div>

            <div className="text-xs font-medium text-[#111]">
              Que {currentIndex + 1}/{dummyQuestions.length}
            </div>
          </div>
        </div>

        <div className="flex-1">
          <div className="w-full">
            <div className="mb-3 mt-5 flex items-start justify-between gap-3 rounded-[10px] border border-purple-300 bg-purple-50 p-4 text-left">
              <p className="text-[13px] font-medium leading-relaxed text-[#222] sm:text-sm lg:text-base">
                {currentQuestion.question}
              </p>
            </div>

            <div className="rounded-xl px-4 py-4 shadow-sm">
              <div className="space-y-2 px-0">
                {currentQuestion.options.map((option) => {
                  const isSelected = selectedOption === option;
                  const optionClass = isSelected
                    ? "border-2 border-[#562566] bg-[#F1EAF6]"
                    : "border border-[#DADADA] bg-white hover:bg-gray-50";

                  return (
                    <button
                      key={option}
                      type="button"
                      disabled={timeUp}
                      onClick={() => setSelectedOption(option)}
                      className={`flex w-full items-center justify-between rounded-lg px-4 py-4 text-left shadow-lg transition ${optionClass}`}
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-all ${
                            isSelected
                              ? "border-[#562566] bg-[#562566]"
                              : "border-gray-400 bg-white"
                          }`}
                        >
                          {isSelected ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-3 w-3 text-white"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={3}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          ) : null}
                        </span>

                        <span className="text-[13px] font-medium text-[#222] sm:text-sm">
                          {option}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={handleSkip}
                  disabled={timeUp}
                  className={`flex items-center gap-1 rounded-lg bg-white px-4 py-2 text-sm font-bold shadow-sm ${
                    timeUp ? "cursor-not-allowed text-gray-400" : "text-[#F5A623]"
                  }`}
                >
                  Skip
                  <FaAngleRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 pb-16">
          <button
            onClick={goToNextQuestion}
            disabled={timeUp}
            className={`h-14 w-full rounded-xl text-sm font-bold shadow-lg ${
              timeUp
                ? "cursor-not-allowed bg-gray-300 text-gray-500"
                : "bg-[#562566] text-white"
            }`}
          >
            {isLastQuestion ? "Submit & View Scorecard" : "Save & Next"}
          </button>
        </div>
      </div>

      {showSubmitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 text-center shadow-2xl">
            <div className="mb-4 flex justify-center">
              <AlertCircle size={36} className="text-red-500" />
            </div>

            <h2 className="mb-3 text-xl font-bold text-[#34163F]">
              Submit Dummy Contest?
            </h2>

            <p className="mb-6 text-sm text-gray-500">
              Some questions are unanswered. You can still submit and view your
              score.
            </p>

            <div className="flex justify-center gap-3">
              <button
                onClick={() => setShowSubmitModal(false)}
                className="rounded-xl bg-gray-100 px-5 py-3 text-sm font-semibold text-gray-600"
              >
                Cancel
              </button>

              <button
                onClick={confirmSubmitContest}
                className="rounded-xl bg-[#562566] px-5 py-3 text-sm font-semibold text-white"
              >
                Yes, Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {timeUp && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm">
          <div className="relative w-full max-w-sm rounded-[30px] bg-white px-6 py-8 text-center shadow-2xl">
            <button
              onClick={() => setTimeUp(false)}
              className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-[#F3ECF7]"
            >
              <X size={22} className="text-[#6B5A7A]" />
            </button>

            <div className="mx-auto mb-5 flex h-32 w-32 items-center justify-center rounded-full bg-[#FF3B30]">
              <img
                src={TimerImage}
                alt="time up"
                className="h-20 w-20 object-contain"
              />
            </div>

            <h2 className="mb-3 text-4xl font-bold text-black">Time UP!</h2>

            <p className="mb-4 text-lg leading-9 text-[#333]">
              The countdown has ended. View your dummy scorecard.
            </p>

            <button
              onClick={() => {
                navigateToResult(answers, 0);
              }}
              className="h-16 w-full rounded-full bg-[#EFEAF7] text-2xl font-medium text-[#562566] shadow-md"
            >
              View Result
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default DummyContest;
